// @givmo/board — the person's own year, read (CBG-YR2-WEB2): the cycles this seat was asked in, the
// year's questionnaire as they are asked it, their own signed answers and their changes, and the
// attention row that names a year still waiting on them.
//
// ══ DARK UNTIL SERVED, AND ONLY EVER THEIR OWN ══════════════════════════════════════════════════
//
// While `BOARD_COI_CYCLE_ENABLED` is off every route here is the route miss, and the reads resolve it
// to `null` ("not served"): the pages are exactly what they were, and the attention list carries no
// such row (the backend withholds the kind). Every read is the person's own, on the organization's
// director door; the backend takes no seat from the client, so nobody else's answers can be asked for.
//
// ══ AN UNSENT ANSWER LIVES ONLY IN THIS TAB ═════════════════════════════════════════════════════
//
// There are no server-side drafts (the contract's §1): nothing half-submitted sits on Givmo's servers,
// and no officer ever sees "started". A forced sign-out must not lose what was typed, so the
// questionnaire keeps a draft in `director-drafts.ts` (sessionStorage, this account and seat, a day at
// most): ONLY what the person typed or changed — a choice, a tick, words typed, an entry taken out by
// its place — never last year's answers the server filled in.

import { useQuery } from '@tanstack/react-query';
import type {
  BoardCoiDirectorCycleOut,
  BoardCoiPreviousOut,
  BoardCoiQuestionAskedOut,
  BoardCoiQuestionnaireForMeOut,
  BoardCoiResponseChangesOut,
  BoardCoiResponseOut,
} from '@givmo/api-client';
import { useBoardDirectorOperations } from '../../board-runtime';
import { isNotServed } from '../../lib/not-served';
import {
  readDirectorDraft,
  type DirectorDraftFields,
  type DirectorDraftScope,
} from '../../director/director-drafts';
import {
  ENTRY_FIELDS,
  KIND_ACKNOWLEDGMENT,
  KIND_TEXT,
  decodeChanges,
  decodeOwnQuestionnaire,
  decodeResponse,
  extractOwnCycles,
  type EntryField,
} from '../conflicts/coi-decode';
import { previousAnswerOf, type AnswerDrafts, type EntryDraft } from '../conflicts/coi-answer-form';

/** The person's own year's query keys, per board. */
export function ownCoiKeys(orgRef: string | null) {
  const root = ['board-director-coi', orgRef ?? ''] as const;
  return {
    root,
    cycles: [...root, 'cycles'] as const,
    questionnaire: (cycleId: string) => [...root, 'questionnaire', cycleId] as const,
    response: (cycleId: string) => [...root, 'response', cycleId] as const,
    changes: (cycleId: string) => [...root, 'changes', cycleId] as const,
  };
}

function retryUnlessNotServed(failureCount: number, error: unknown): boolean {
  return !isNotServed(error) && failureCount < 1;
}

/** The years this seat was asked in, newest first: `null` while the year is not served here. */
export function useOwnCoiCycles(orgRef: string | null, enabled: boolean) {
  const operations = useBoardDirectorOperations();
  return useQuery({
    queryKey: ownCoiKeys(orgRef).cycles,
    enabled: enabled && orgRef !== null,
    retry: retryUnlessNotServed,
    queryFn: async (): Promise<BoardCoiDirectorCycleOut[] | null> => {
      try {
        const { data } = await operations.directorListOwnBoardCoiCycles({
          path: { org_ref: orgRef ?? '' },
          throwOnError: true,
        });
        return extractOwnCycles(data);
      } catch (error) {
        if (isNotServed(error)) return null;
        throw error;
      }
    },
  });
}

/** This person's questionnaire for one year (§13.3 D2). */
export function useOwnCoiQuestionnaire(
  orgRef: string | null,
  cycleId: string | null,
  enabled: boolean,
) {
  const operations = useBoardDirectorOperations();
  return useQuery({
    queryKey: ownCoiKeys(orgRef).questionnaire(cycleId ?? ''),
    enabled: enabled && orgRef !== null && cycleId !== null,
    retry: retryUnlessNotServed,
    queryFn: async (): Promise<BoardCoiQuestionnaireForMeOut | null> => {
      const { data } = await operations.directorGetOwnBoardCoiQuestionnaire({
        path: { org_ref: orgRef ?? '', cycle_id: cycleId ?? '' },
        throwOnError: true,
      });
      return decodeOwnQuestionnaire(data);
    },
  });
}

/** This person's own answers for one year, at the head of their corrections. */
export function useOwnCoiResponse(orgRef: string | null, cycleId: string | null, enabled: boolean) {
  const operations = useBoardDirectorOperations();
  return useQuery({
    queryKey: ownCoiKeys(orgRef).response(cycleId ?? ''),
    enabled: enabled && orgRef !== null && cycleId !== null,
    retry: retryUnlessNotServed,
    queryFn: async (): Promise<BoardCoiResponseOut | null> => {
      const { data } = await operations.directorGetOwnBoardCoiResponse({
        path: { org_ref: orgRef ?? '', cycle_id: cycleId ?? '' },
        throwOnError: true,
      });
      return decodeResponse(data);
    },
  });
}

/** This person's answers against the year before. */
export function useOwnCoiChanges(orgRef: string | null, cycleId: string | null, enabled: boolean) {
  const operations = useBoardDirectorOperations();
  return useQuery({
    queryKey: ownCoiKeys(orgRef).changes(cycleId ?? ''),
    enabled: enabled && orgRef !== null && cycleId !== null,
    retry: retryUnlessNotServed,
    queryFn: async (): Promise<BoardCoiResponseChangesOut | null> => {
      const { data } = await operations.directorGetOwnBoardCoiResponseChanges({
        path: { org_ref: orgRef ?? '', cycle_id: cycleId ?? '' },
        throwOnError: true,
      });
      return decodeChanges(data);
    },
  });
}

// ── The attention row ─────────────────────────────────────────────────────────────────────────────

/** A year waiting on this seat (the attention read's `coi_disclosure` kind, §13.4). */
export interface CoiDisclosureAwaiting {
  cycleId: string;
  label: string;
  dueOn: string;
  /** CBG-YR4-COI-JOINER: a closed year sent to someone who joined after it closed (no due date). */
  joinedAfterClose: boolean;
}

/**
 * The `coi_disclosure` entries of the attention list: a year sent to this seat, open, and not yet
 * answered. The backend applies the rule (and withholds the kind while the year is dark); nothing here
 * re-derives it. Any other kind, and a malformed row, is skipped.
 */
export function coiDisclosuresAwaiting(items: unknown): CoiDisclosureAwaiting[] {
  if (!Array.isArray(items)) return [];
  const rows: CoiDisclosureAwaiting[] = [];
  for (const raw of items) {
    if (typeof raw !== 'object' || raw === null) continue;
    const item = raw as Record<string, unknown>;
    if (item.kind !== 'coi_disclosure') continue;
    if (typeof item.cycle_id !== 'string' || item.cycle_id.length === 0) continue;
    rows.push({
      cycleId: item.cycle_id,
      label: typeof item.label === 'string' ? item.label : '',
      dueOn: typeof item.due_on === 'string' ? item.due_on : '',
      joinedAfterClose: item.joined_after_close === true,
    });
  }
  return rows;
}

// ── The draft: only what the person typed or changed ─────────────────────────────────────────────

/**
 * What to keep of the form: per question, a choice or tick the person made (`<id>.value`), words they
 * typed (`<id>.details`, `<id>.text`), carried entries they took out by their PLACE in last year's
 * list (`<id>.removed`), carried entries they edited (`<id>.edit.<place>.<field>`), and entries they
 * added (`<id>.new.<n>.<field>`). Nothing the server filled in is written.
 */
export function questionnaireDraftFields(
  questions: readonly BoardCoiQuestionAskedOut[],
  previous: BoardCoiPreviousOut | null | undefined,
  initial: AnswerDrafts,
  drafts: AnswerDrafts,
): DirectorDraftFields {
  const fields: Record<string, string> = {};
  for (const question of questions) {
    const id = question.id;
    const start = initial[id];
    const now = drafts[id];
    if (start === undefined || now === undefined) continue;
    if (question.kind === KIND_ACKNOWLEDGMENT) {
      if (now.value === true) fields[`${id}.value`] = 'true';
      continue;
    }
    if (question.kind === KIND_TEXT) {
      if (typeof now.value === 'string' && now.value !== start.value)
        fields[`${id}.text`] = now.value;
      continue;
    }
    if (typeof now.value === 'string' && now.value !== start.value)
      fields[`${id}.value`] = now.value;
    if (now.details !== start.details) fields[`${id}.details`] = now.details;
    const before = previousAnswerOf(previous, id)?.entries ?? [];
    const kept = new Set(now.entries.map((entry) => entry.key).filter((key) => key !== null));
    const removed = before
      .map((entry, place) => (kept.has(entry.key) ? null : place))
      .filter((place): place is number => place !== null);
    if (removed.length > 0) fields[`${id}.removed`] = removed.join(',');
    now.entries.forEach((entry, index) => {
      if (entry.key === null) {
        for (const [field, value] of Object.entries(entry.fields)) {
          if (value && value.trim().length > 0) fields[`${id}.new.${index}.${field}`] = value;
        }
        return;
      }
      const place = before.findIndex((candidate) => candidate.key === entry.key);
      const original = before[place];
      if (original === undefined) return;
      for (const field of [...ENTRY_FIELDS, 'details'] as const) {
        const typed = entry.fields[field] ?? '';
        if (typed !== (original[field] ?? '')) fields[`${id}.edit.${place}.${field}`] = typed;
      }
    });
  }
  return fields;
}

/** Put a kept draft back over the form's starting state (the same rules, read the other way). */
export function applyQuestionnaireDraft(
  questions: readonly BoardCoiQuestionAskedOut[],
  previous: BoardCoiPreviousOut | null | undefined,
  initial: AnswerDrafts,
  kept: DirectorDraftFields,
): AnswerDrafts {
  const next: AnswerDrafts = {};
  for (const question of questions) {
    const id = question.id;
    const start = initial[id];
    if (start === undefined) continue;
    const draft = { ...start, entries: [...start.entries], removed: [...start.removed] };
    if (question.kind === KIND_ACKNOWLEDGMENT) {
      draft.value = kept[`${id}.value`] === 'true';
      next[id] = draft;
      continue;
    }
    if (question.kind === KIND_TEXT) {
      const text = kept[`${id}.text`];
      if (typeof text === 'string') draft.value = text;
      next[id] = draft;
      continue;
    }
    const value = kept[`${id}.value`];
    if (value === 'yes' || value === 'no') draft.value = value;
    const details = kept[`${id}.details`];
    if (typeof details === 'string') draft.details = details;
    const before = previousAnswerOf(previous, id)?.entries ?? [];
    const removedPlaces = new Set(
      (kept[`${id}.removed`] ?? '')
        .split(',')
        .filter((part) => part.length > 0)
        .map(Number),
    );
    const removedKeys = new Set(
      before.filter((_entry, place) => removedPlaces.has(place)).map((entry) => entry.key),
    );
    draft.removed = draft.entries.filter(
      (entry) => entry.key !== null && removedKeys.has(entry.key),
    );
    draft.entries = draft.entries
      .filter((entry) => entry.key === null || !removedKeys.has(entry.key))
      .map((entry): EntryDraft => {
        const place = before.findIndex((candidate) => candidate.key === entry.key);
        if (place === -1) return entry;
        const fields = { ...entry.fields };
        for (const field of [...ENTRY_FIELDS, 'details'] as const) {
          const typed = kept[`${id}.edit.${place}.${field}`];
          if (typeof typed === 'string') fields[field as EntryField] = typed;
        }
        return { ...entry, fields };
      });
    const added = new Map<string, EntryDraft>();
    for (const [name, text] of Object.entries(kept)) {
      const match = new RegExp(`^${id.replace(/[-]/g, '\\-')}\\.new\\.(\\d+)\\.(\\w+)$`).exec(name);
      if (match === null) continue;
      const slot = match[1] ?? '';
      const entry = added.get(slot) ?? { localId: `kept-${id}-${slot}`, key: null, fields: {} };
      entry.fields[(match[2] ?? 'name') as EntryField] = text;
      added.set(slot, entry);
    }
    if (added.size > 0) {
      draft.entries = [...draft.entries, ...[...added.values()]];
      if (draft.value !== 'yes') draft.value = 'yes';
    }
    next[id] = draft;
  }
  return next;
}

/** Whether answers wait unsent in this tab for a year (the year card's "Continue"). */
export function hasKeptAnswers(scope: DirectorDraftScope | null): boolean {
  return scope !== null && readDirectorDraft(scope) !== null;
}
